import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Form,
  FormFeedback,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";

import { ActivityButton, SequenceNumberInput } from "../../../components/Common";
import {
  AddGradeToStageRequest,
  EducationalStageGradeDto,
} from "../../../interfaces/curriculum";
import { SequenceEnum } from "../../../enums";
import {
  useAddGradeToEducationalStageMutation,
  useUpdateGradeInEducationalStageMutation,
} from "../../../store/api/educationalStageApi";

type TProps = {
  stageId: string | null;
  grade?: EducationalStageGradeDto | null;
  show: boolean;
  onCloseClick: () => void;
};

export const AddGradeModal: React.FC<TProps> = ({
  stageId,
  grade,
  show,
  onCloseClick,
}) => {
  const { t } = useTranslation();
  const isEdit = !!grade?.id;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [addGrade, { isLoading: isAdding }] =
    useAddGradeToEducationalStageMutation();
  const [updateGrade, { isLoading: isUpdating }] =
    useUpdateGradeInEducationalStageMutation();
  const isLoading = isAdding || isUpdating;

  const getEmptyValues = (): AddGradeToStageRequest => ({
    name: { en: "", ar: "" },
    sequenceNumber: 0,
  });

  const form = useFormik<AddGradeToStageRequest>({
    enableReinitialize: false,
    initialValues: getEmptyValues(),
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(100, t("maxLength", { maxLength: 100, displayName: "englishValue" }))
          .required(t("isNotEmpty", { displayName: "englishValue" })),
        ar: Yup.string()
          .max(100, t("maxLength", { maxLength: 100, displayName: "arabicValue" }))
          .required(t("isNotEmpty", { displayName: "arabicValue" })),
      }),
      sequenceNumber: Yup.number().required(
        t("isNotEmpty", { displayName: "sequenceNumber" }),
      ),
    }),
    onSubmit: async (values) => {
      if (!stageId) return;
      try {
        setSubmitError(null);
        const payload = {
          name: values.name,
          sequenceNumber: Number(values.sequenceNumber),
        };
        if (isEdit && grade?.id) {
          await updateGrade({
            stageId,
            gradeId: grade.id,
            data: payload,
          }).unwrap();
        } else {
          await addGrade({
            stageId,
            data: payload,
          }).unwrap();
        }
        form.resetForm({ values: getEmptyValues() });
        onCloseClick();
      } catch (err: any) {
        setSubmitError(err?.data?.message || "An error occurred");
      }
    },
  });

  const onClose = () => {
    form.resetForm({ values: getEmptyValues() });
    setSubmitError(null);
    onCloseClick();
  };

  useEffect(() => {
    if (!show) {
      form.resetForm({ values: getEmptyValues() });
      setSubmitError(null);
      return;
    }

    if (grade) {
      form.setValues({
        name: {
          en: grade.name?.en || "",
          ar: grade.name?.ar || "",
        },
        sequenceNumber: grade.sequenceNumber ?? 0,
      });
      return;
    }

    form.resetForm({ values: getEmptyValues() });
  }, [show, grade]);

  return (
    <Modal id="addEditGradeModal" isOpen={show} toggle={onClose} centered>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
      >
        <ModalHeader className="p-3" toggle={onClose}>
          {isEdit ? t("editItem", { item: "grade" }) : t("addGrade")}
        </ModalHeader>
        <ModalBody>
          {submitError ? (
            <p className="text-danger small">{submitError}</p>
          ) : null}
          <SequenceNumberInput
            tabIndex={1}
            value={form.values.sequenceNumber}
            onChange={(e) => {
              e.preventDefault();
              const { value } = e.target;
              const regex =
                /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
              if (regex.test(value.toString())) {
                form.setFieldValue("sequenceNumber", value);
              } else {
                form.setFieldValue("sequenceNumber", "");
              }
            }}
            onBlur={form.handleBlur}
            invalid={
              !!(form.touched.sequenceNumber && form.errors.sequenceNumber)
            }
            sequenceType={SequenceEnum.GRADE}
            onGenerateSequenceNumber={(v: number) => {
              form.setFieldValue("sequenceNumber", v);
            }}
            mode={isEdit ? "edit" : "add"}
            request={{
              type: SequenceEnum.GRADE,
              educationalStageId: stageId || undefined,
            }}
          />
          {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
            <FormFeedback type="invalid" className="d-block">
              {form.errors.sequenceNumber as string}
            </FormFeedback>
          ) : null}
          <div className="mb-3">
            <label className="form-label">
              {t("nameEn")}
              <span className="text-danger">*</span>
            </label>
            <Input
              name="name.en"
              value={form.values.name.en}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={!!(form.touched.name?.en && form.errors.name?.en)}
            />
            {form.touched.name?.en && form.errors.name?.en ? (
              <FormFeedback type="invalid">
                {form.errors.name.en as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("nameAr")}
              <span className="text-danger">*</span>
            </label>
            <Input
              style={{ fontFamily: "aljannat", direction: "rtl" }}
              name="name.ar"
              value={form.values.name.ar}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={!!(form.touched.name?.ar && form.errors.name?.ar)}
            />
            {form.touched.name?.ar && form.errors.name?.ar ? (
              <FormFeedback type="invalid">
                {form.errors.name.ar as string}
              </FormFeedback>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Link to={"#"} onClick={onClose} className="btn btn-link link-success">
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title="save"
            inProgressTitle="saving"
            color="success"
            type="submit"
            inProgress={isLoading}
            disabled={isLoading || !stageId}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};
